import { FormGroup, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { Component, OnInit, Input, ViewContainerRef, ElementRef, ViewChild, Renderer2, Output, EventEmitter } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { HasFormComponent } from '../../../core/interfaces/hasFormComponent';
import { NdmsInputComponent } from '../ndms-input/ndms-input.component';

@Component({
  selector: 'ndms-form-control',
  templateUrl: './ndms-form-control.component.html',
  styleUrls: ['./ndms-form-control.component.scss']
})
export class NdmsFormControlComponent implements OnInit {
  @Input() field: string;
  @Input() label: string;
  @Input() labelHidden: boolean;
  @Input() size: string;
  @Input() style: string;
  @Input() disabled: boolean;
  @Input() group: FormGroup;
  @Input() hasFormComponent: HasFormComponent<any>;
  @Input() array: string;
  @Input() lookup: string;
  @Input() lookupStartKey: string;
  @Input() lookupEndKey: string;
  @Input() placeholder: string;
  @Input() length: number;
  @Input() max: number = 1000;
  @Input() min: number;
  @Input() mask: string;
  @Input() readonly: boolean;
  @Input() displayOnly: boolean;
  @Input() isEditing: () => boolean;
  @Input() editable: boolean;
  @Input() isFirstTabbedControl: boolean;
  @Input() isLastTabbedControl: boolean;
  @Input() hideType: boolean;
  @Input() required: string;
  @Input() uniqueId: string;
  @Output() tabExit = new EventEmitter<any>();
  @Input() helpText: string;

  //@ViewChild('firstElement') firstElement: ElementRef;
  //@ViewChild('lastElement') lastElement: ElementRef;

  protected idGuid = UUID.UUID().substr(9, 4);

  public readonly ControlState = ControlState;
  protected childVcRef: ViewContainerRef;

  constructor(vcRefBase: ViewContainerRef) {
    let parentComponent = (<any>vcRefBase.injector).view.component;
    if (parentComponent instanceof NdmsInputComponent) {
      let ndmsInputComponent = parentComponent as NdmsInputComponent;
      if(ndmsInputComponent === null) return;
      this.field = ndmsInputComponent.field;
      this.label = ndmsInputComponent.label;
      this.labelHidden = ndmsInputComponent.labelHidden;
      this.size = ndmsInputComponent.size;
      this.group = ndmsInputComponent.parentComponent.form;
      this.array = ndmsInputComponent.array;
      this.hasFormComponent = ndmsInputComponent.hasFormComponent;
      this.lookup = ndmsInputComponent.lookup;
      this.placeholder = ndmsInputComponent.placeholder;
      this.style = ndmsInputComponent.style;
      this.length = ndmsInputComponent.length;
      this.max = ndmsInputComponent.max;
      this.min = ndmsInputComponent.min;
      this.mask = ndmsInputComponent.mask;
      this.isEditing = () => { return ndmsInputComponent.hasFormComponent.isEditing; };
      this.readonly = ndmsInputComponent.readonly;
      this.displayOnly = ndmsInputComponent.displayOnly;
      this.editable = ndmsInputComponent.editable;
      this.isFirstTabbedControl = ndmsInputComponent.isFirstTabbedControl;
      this.isLastTabbedControl = ndmsInputComponent.isLastTabbedControl;
      this.tabExit = ndmsInputComponent.tabExit;
    }
    else {
      this.group = (<any>parentComponent).form ? (<any>parentComponent).form : parentComponent.group;
      this.isEditing = () => { return <boolean>parentComponent.isEditing; };
      //this.isEditing =   parentComponent.isEditing;
      this.hasFormComponent = this.findHasFormComponentParent(vcRefBase);
    }
  }

  ngOnInit() {
  }

  private findHasFormComponentParent(viewContainerRef: ViewContainerRef): HasFormComponent<any> {

    let currentVcr: ViewContainerRef = viewContainerRef;
    let currentParentVcr = (<any>currentVcr.injector).view;
    //if(currentParentVcr === null || currentVcr === null || !(currentParentVcr.component instanceof HasFormComponent))return;

    while (!(currentParentVcr.component instanceof HasFormComponent)) {
      currentParentVcr = currentParentVcr.parent;
    }

    return currentParentVcr.component as HasFormComponent<any>;
  }


  get isReadOnly(): boolean {
    return this.readonly;
  }

  get value() {
    let fieldFormControl = this.group.get(this.field);

    if (!fieldFormControl) {
      throw `Form field '${this.field}' not found.  Are you sure you added it to the FormGroup?`;
    }

    return fieldFormControl.value;
  }

  get arrayValue() {
    let array = this.group.get(this.array);
    return array ? array.value : []
  }

  private getControlSize(size: string) {
    if (!size) {
      return ''
    }
    switch (size) {
      case 'sm':
        return 'col-xs-3';
      case 'md':
        return 'col-xs-6';
      case 'lg':
        return 'col-xs-12';
      default:
        if (!isNaN(Number(size))) {
          return 'col-xs-' + size;
        }
        else {
          return 'col-xs-12 ' + size;
        }
    }
  }

  isFieldValid(field: string) {
    let formField = this.group.get(field);

    if (formField) {
      return formField.valid || !formField.touched;
    }

    return true;
  }

  getFieldErrors(field: string) {
    let formField = this.group.get(field);

    if (formField) {
      return formField.errors;
    }

    return null;
  }

  addToArray() {
    let formArrayControls: FormArray = (this.group.get(this.array) as FormArray)

    const formArrayDefaultObject = this.hasFormComponent.formArrayDefaultObjectRegistry[this.array];

    if (!formArrayDefaultObject) {
      throw ("Must register default array type for field: '" + this.array + "' for this form.");
    }

    formArrayControls.push(formArrayDefaultObject());
  };

  canAddArrayItem(currentIndex: number) {
    throw ("Method 'canAddArrayItem(currentIndex: number)' must be implemented by derived form control component.");
  }

  private lastArrayItem(index) {
    return index == (this.group as any).get(this.array).controls.length - 1
  }

  private removeFromArray(index) {
    ((this.group as any).get(this.array).controls as Array<FormControl>).splice(index, 1);
  }

  protected getLookups(lookup?: string) {
    var lookups = this.hasFormComponent.lookupService.get(lookup || this.lookup);

    lookups = (this.lookupStartKey) ? this.filterLookupsByStartKey(lookups) : lookups;
    lookups = (this.lookupEndKey) ? this.filterLookupsByEndKey(lookups) : lookups;

    return lookups;
  }

  protected filterLookupsByStartKey(lookups: Array<any>) {
    let filteredLookups: Array<any> = new Array<any>();
    let valueValid: boolean = false;

    if (this.lookupStartKey && lookups) {
      let collecting: boolean = false;
      for (let entry of lookups) {
        if (entry.key === this.lookupStartKey && !collecting) {
          collecting = true;
        }

        if (collecting) {
          filteredLookups.push(entry);
          if (this.group.controls[this.field].value && entry.key === this.group.controls[this.field].value.key) {
            valueValid = true;
          }
        }
      }
    }

    if (!valueValid) {
      this.group.controls[this.field].patchValue(undefined);
    }

    return filteredLookups;
  }

  protected filterLookupsByEndKey(lookups: Array<any>) {
    let filteredLookups: Array<any> = new Array<any>();
    let valueValid: boolean = false;

    if (this.lookupEndKey && lookups) {
      for (let entry of lookups) {
        if (entry.key === this.lookupEndKey) {
          break;
        }

        filteredLookups.push(entry);
        if (this.group.controls[this.field].value && entry.key === this.group.controls[this.field].value.key) {
          valueValid = true;
        }
      }
    }

    if (!valueValid) {
      this.group.controls[this.field].patchValue(undefined);
    }

    return filteredLookups;
  }

  getTemplate(): ControlState {
    if (this.displayOnly || (this.editable && !this.isEditing())) {
      return ControlState.View;
    }
    else {
      return !this.array ? ControlState.Edit : ControlState.Array;
    }
  }

  get id() {
    return this.field + '_' + this.idGuid;
  }
}

export enum ControlState {
  View,
  Edit,
  Array
}

export enum CheckRadioType {
  Inline,
  Stacked
}
